import React, { useEffect, useRef, forwardRef } from "react";
import {
  cn,
  getFocusRingStyles,
  getBorderStyles,
  getBackgroundStyles,
  getForegroundStyles,
  getDisabledStyles,
} from "../../styles";
import InputMask from "@mona-health/react-input-mask";
import { Button, ButtonProps, ButtonVariant, Typography } from "../../atoms";
import { ThemeColor, PanelColor } from "../../props";

export interface InputProps {
  /**
   * Additional CSS class name(s) for the input container.
   */
  className?: string;
  /**
   * Unique identifier for the input element.
   * Used for accessibility and form associations.
   */
  id?: string;
  /**
   * Current value of the input field.
   */
  value: string | null;
  /**
   * Callback function called when the input value changes.
   */
  onChange: (value: string) => void;
  /**
   * Placeholder text shown when the input is empty.
   */
  placeholder?: string;
  /**
   * Automatically focuses the input when mounted.
   * @default false
   */
  autoFocus?: boolean;
  /**
   * Whether the input is disabled.
   * @default false
   */
  disabled?: boolean;
  /**
   * Callback function called when a key is pressed.
   * Useful for handling keyboard shortcuts or navigation.
   */
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  /**
   * Callback function called when the input receives focus.
   */
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  /**
   * HTML input type attribute (text, email, password, number, etc.).
   * @default "text"
   */
  inputType?: React.HTMLInputTypeAttribute;
  /**
   * Whether the input is required for form submission.
   * @default false
   */
  required?: boolean;
  /**
   * Applies bold font weight to the input text.
   * @default false
   */
  strong?: boolean;
  /**
   * Reduces spacing and font sizes for a more compact appearance.
   * @default false
   */
  dense?: boolean;
  /**
   * Applies error styling with red border and focus ring.
   * @default false
   */
  error?: boolean;
  /**
   * Text to display before the input value.
   * Automatically adjusts input padding to accommodate the prefix.
   */
  prefix?: string;
  /**
   * Text to display after the input value.
   * Automatically adjusts input padding to accommodate the postfix.
   */
  postfix?: string;
  /**
   * Input mask pattern for formatting (e.g., phone numbers, dates).
   * Uses react-input-mask format.
   */
  mask?: string;
  /**
   * Step value for number inputs.
   * Defines the increment/decrement interval.
   */
  step?: string | number;
  /**
   * Background color theme for the input.
   * @default PanelColor.level0
   */
  color?: PanelColor;
  /**
   * HTML autocomplete attribute for browser autofill behavior.
   */
  autoComplete?: string;
  /**
   * Makes the input non-editable while still allowing text selection.
   */
  readOnly?: boolean;
  /**
   * ARIA attribute indicating invalid state for accessibility.
   */
  "aria-invalid"?: boolean;
  /**
   * ARIA attribute referencing helper or error text for accessibility.
   */
  "aria-describedby"?: string;
  /**
   * Callback to intercept and modify mask state transitions.
   * Receives current, previous, and next states; must return the resolved next state.
   */
  beforeMaskedStateChange?: (args: {
    currentState?: {
      value: string;
      selection: { start: number | null; end: number | null };
    };
    previousState?: {
      value: string;
      selection: { start: number | null; end: number | null };
    };
    nextState: {
      value: string;
      selection: { start: number | null; end: number | null };
    };
  }) => {
    value: string;
    selection: { start: number | null; end: number | null };
  };
  /**
   * Props for a right-aligned text-style icon button (e.g. toggle password visibility).
   * Renders a text-variant Button; pass iconPath/iconName/iconLocal and onClick at minimum.
   */
  actionButton?: Partial<ButtonProps>;
}

/**
 * Flexible input component with support for masks, prefixes, postfixes, and error states.
 * Provides consistent styling with customizable appearance through color themes and density.
 * Automatically manages padding when prefix/postfix are provided and supports input masking
 * for formatted inputs like phone numbers or dates. Built with accessibility in mind with
 * proper ARIA attributes and keyboard navigation support.
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      id,
      value,
      onChange,
      placeholder,
      autoFocus,
      disabled,
      onKeyDown,
      onFocus,
      inputType = "text",
      required,
      strong,
      dense = false,
      error = false,
      prefix,
      postfix,
      mask,
      step,
      color = PanelColor.level0,
      autoComplete,
      actionButton,
      readOnly,
      ...props
    },
    ref,
  ) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const prefixRef = useRef<HTMLSpanElement>(null);
    const postfixRef = useRef<HTMLSpanElement>(null);
    const actionButtonRef = useRef<HTMLButtonElement>(null);

    const setInputRef = (element: HTMLInputElement | null) => {
      inputRef.current = element;
      if (typeof ref === "function") {
        ref(element);
      } else if (ref) {
        ref.current = element;
      }
    };

    useEffect(() => {
      if (!disabled && autoFocus && inputRef.current) {
        inputRef.current.focus();
      }
    }, [disabled, autoFocus]);

    useEffect(() => {
      if (prefix && prefixRef.current && inputRef.current) {
        requestAnimationFrame(() => {
          const prefixWidth = prefixRef.current!.offsetWidth;
          const padding = dense ? 6 : 8;
          inputRef.current!.style.paddingLeft = `${prefixWidth + padding}px`;
        });
      } else if (inputRef.current) {
        const padding = dense ? 6 : 8;
        inputRef.current.style.paddingLeft = `${padding}px`;
      }
    }, [prefix, dense]);

    useEffect(() => {
      if (postfix && postfixRef.current && inputRef.current) {
        requestAnimationFrame(() => {
          const postfixWidth = postfixRef.current!.offsetWidth;
          const padding = dense ? 6 : 8;
          inputRef.current!.style.paddingRight = `${postfixWidth + padding}px`;
        });
      } else if (actionButton && actionButtonRef.current && inputRef.current) {
        requestAnimationFrame(() => {
          const buttonWidth = actionButtonRef.current!.offsetWidth;
          inputRef.current!.style.paddingRight = `${buttonWidth}px`;
        });
      } else if (inputRef.current) {
        const padding = dense ? 6 : 8;
        inputRef.current.style.paddingRight = `${padding}px`;
      }
    }, [postfix, actionButton, dense]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange(e.target.value);
    };

    return (
      <div className={cn("relative flex items-center", className)}>
        {prefix && (
          <Typography.Span
            ref={prefixRef}
            className={cn(
              "absolute",
              dense ? "left-1.5 text-xs" : "left-2 text-sm",
            )}
            muted
            {...{ strong, disabled }}
          >
            {prefix}
          </Typography.Span>
        )}
        <InputMask
          mask={mask}
          value={value || ""}
          onChange={handleChange}
          disabled={disabled}
          readOnly={readOnly}
          onFocus={onFocus}
          ref={setInputRef}
          {...props}
        >
          <input
            onKeyDown={onKeyDown}
            id={id}
            autoFocus={autoFocus}
            autoComplete={autoComplete ?? "off"}
            type={inputType}
            required={required}
            placeholder={placeholder}
            step={step}
            className={cn(
              "flex-1 pr-2 rounded-md border",
              dense ? "h-[30px] py-1.5 text-xs" : "min-h-[38px] py-2 text-sm",
              dense ? "rounded" : "rounded-md",
              "focus:outline-none focus:ring-2",
              error
                ? getFocusRingStyles(ThemeColor.error)
                : getFocusRingStyles(ThemeColor.primary),
              getBackgroundStyles(color),
              getBorderStyles(error ? ThemeColor.error : ThemeColor.gray),
              getForegroundStyles(color),
              "placeholder:text-current placeholder:opacity-50",
              strong && "font-medium",
              disabled && getDisabledStyles(),
            )}
          />
        </InputMask>
        {postfix && (
          <Typography.Span
            ref={postfixRef}
            className={cn(
              "absolute",
              dense ? "right-1.5 text-xs" : "right-2 text-sm",
            )}
            muted
            {...{ strong, disabled }}
          >
            {postfix}
          </Typography.Span>
        )}
        {actionButton && (
          <Button
            ref={actionButtonRef}
            variant={ButtonVariant.text}
            muted
            disabled={disabled}
            dense={dense}
            tabIndex={-1}
            {...actionButton}
            className={cn("absolute right-0", actionButton.className)}
          />
        )}
      </div>
    );
  },
);

Input.displayName = "Input";
